import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as roomTypes from '../../room-types'

import * as gql from './gql'
import type { TypeParams } from './types'

const SPACE_DIMENSION_ID = ents.core.dms.models.Dimension.Id.from(
  ents.extra.quiver.resources.data.dimensions.space.RESOURCE.payload,
)

/** Record lab assignments API, for fetching space record lab assignments */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(dms.schema.Feature)

  /** @see {@link roomTypes.Collection} */
  private readonly _roomTypesEc = ngCore.inject(roomTypes.Collection)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => {
      if (!context) {
        return rxjs.of([])
      }

      return gql.queries.recordLabAssignments.executor(context)
    },
  )

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (
    context,
    { labIds },
  ) => {
    if (!context) {
      return rxjs.of(undefined)
    }

    return gql.mutations.addLabsToLabService.executor({
      ...context,
      labServiceId: context.spaceRecordId,
      labIds,
    })
  }

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    if (!context) {
      return rxjs.of(undefined)
    }

    return gql.mutations.removeLabAssignment.executor({
      ...context,
      assignmentId: id,
    })
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs
      .combineLatest({
        schema: this._schemaFeature.selectors.schema.asObservable,
        spaceRoomTypesLookup:
          this._roomTypesEc.plugins.cachePlugin.feature.selectors.lookup
            .asObservable,
      })
      .pipe(
        // NOTE: The schema starts as an empty placeholder, so wait for the
        // loaded one to avoid emitting columns without space entities
        rxjs.filter(({ schema }) => schema.areModelsInitialized),
        rxjs.map(({ schema, spaceRoomTypesLookup }) => [
          ...schema
            .getEntitiesInDimension(SPACE_DIMENSION_ID)
            .map<ec.models.Attribute<TypeParams>>(entity => ({
              getValueFrom: ({ spaceRecordLookup }) =>
                spaceRecordLookup[entity.id]?.name,
              id: entity.id,
              name: entity.name,
            })),
          {
            getValueFrom: record =>
              spaceRoomTypesLookup?.[record.roomTypeId]?.name,
            id: 'roomType',
            name: 'Room Type',
          },
        ]),
      )
}
